// ─────────────────────────────────────────────
// KLOTHIQ — Dynamic Product Form Page (Master Config Driven)
// ─────────────────────────────────────────────

import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { useAuth } from '@/contexts/AuthContext';
import { useProduct, useCreateProduct, useUpdateProduct, useCreateVariant, useUpdateVariant, getNextProductCodeSequence } from '@/hooks/useProducts';
import {
  useProductTypes,
  useProductTypeAttributes,
  useTargetAudiences,
  useAgeGroups,
  useTeams,
  useCategories,
  useSizeSets,
  Attribute,
} from '@/hooks/useMasterConfig';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useState as useLocalState } from 'react';
import { buildProductCodePrefix, formatProductCode, buildVariantSku, getNextSize } from '@/utils/productCodeUtils';
import { Trash2, Loader2, Sparkles, Package, Tags, Layers, PlusCircle, Save, Copy, Zap, ChevronDown, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// ── Form schemas ──────────────────────────────────────────────
const variantSchema = z.object({
  id: z.string().optional(),
  size: z.string().min(1, 'Size is required'),
  color: z.string().optional(),
  sku: z.string().min(1, 'SKU is required').toUpperCase(),
  default_cost: z.number({ coerce: true }).min(0, 'Cost must be 0 or more'),
  default_price: z.number({ coerce: true }).min(0, 'Price must be 0 or more'),
  initial_stock: z.number({ coerce: true }).int().min(0).default(0),
  low_stock_level: z.number({ coerce: true }).int().min(0).default(5),
});

const productSchema = z.object({
  product_code: z.string().min(1, 'Product code is required').toUpperCase(),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  category_id: z.string().optional(),
  product_type_id: z.string().optional(),
  target_audience_id: z.string().optional(),
  age_group_id: z.string().optional(),
  description: z.string().optional(),
  attributes: z.record(z.any()).default({}),
  variants: z.array(variantSchema).min(1, 'At least one variant is required'),
});

type ProductFormValues = z.infer<typeof productSchema>;

function Field({
  id, label, error, required, children, className = ''
}: {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm font-medium text-foreground/90">
        {label}{required && <span className="ml-1 text-destructive font-bold">*</span>}
      </label>
      {children}
      {error && <p className="text-[13px] font-medium text-destructive mt-1">{error}</p>}
    </div>
  );
}

const inputCls =
  'flex h-11 w-full rounded-lg border border-input/60 bg-background/50 hover:bg-background px-3 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50 disabled:bg-muted/50 shadow-sm';

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const confirm = useConfirm();
  const { organization } = useAuth();
  const [showSizeSetMenu, setShowSizeSetMenu] = useLocalState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useLocalState(false);
  const [codePrefix, setCodePrefix] = useLocalState<string>('');
  // Hard submit guard — fires synchronously before any state update to close the
  // double-submit race window that isPending alone cannot cover.
  const isInFlight = useRef(false);

  const { data: existing, isLoading: loadingProduct, error: productError } = useProduct(id);
  const { data: productTypes } = useProductTypes();
  const { data: targetAudiences } = useTargetAudiences();
  const { data: ageGroups } = useAgeGroups();
  const { data: teams } = useTeams();
  const { data: categories } = useCategories();
  const { data: sizeSets } = useSizeSets();

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const createVariant = useCreateVariant();
  const updateVariant = useUpdateVariant();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      product_code: '',
      name: '',
      category_id: '',
      product_type_id: '',
      target_audience_id: '',
      age_group_id: '',
      description: '',
      attributes: {},
      variants: [{ size: '', color: '', sku: '', default_cost: 0, default_price: 0, initial_stock: 0, low_stock_level: 5 }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'variants' });

  const watchName = watch('name');
  const watchTypeId = watch('product_type_id');
  const watchAttrs = watch('attributes') || {};
  const watchCode = watch('product_code');

  // ── Live Code Prefix Preview Calculation ──────────────────────
  useEffect(() => {
    if (isEditing) return;
    const selectedType = productTypes?.find((pt: any) => ((pt as any).id || pt.code || pt.name) === watchTypeId);
    const typeName = selectedType?.name || selectedType?.code || '';

    const prefix = buildProductCodePrefix({
      productTypeName: typeName,
      teamName: watchAttrs.TEAM || watchAttrs.team || watchAttrs.Team,
      edition: watchAttrs.EDITION || watchAttrs.edition || watchAttrs.Edition,
      sleeve: watchAttrs.SLEEVE_TYPE || watchAttrs.sleeve || watchAttrs.Sleeve,
      collar: watchAttrs.COLLAR_TYPE || watchAttrs.collar || watchAttrs.Collar,
      zipper: !!(watchAttrs.ZIPPER || watchAttrs.zipper),
      player: watchAttrs.PLAYER_NAME || watchAttrs.player || watchAttrs.Player,
      playerNumber: watchAttrs.PLAYER_NUMBER || watchAttrs.player_number || watchAttrs.Number,
      productName: watchName,
    });
    setCodePrefix(prefix);
  }, [watchName, watchTypeId, watchAttrs, productTypes, isEditing]);

  // ── Controlled Product Code Generator (DB Sequence Driven) ────
  const autoGenerateCode = async () => {
    if (isEditing) return;
    setIsGeneratingCode(true);
    try {
      const selectedType = productTypes?.find((pt: any) => ((pt as any).id || pt.code || pt.name) === watchTypeId);
      const typeName = selectedType?.name || selectedType?.code || '';

      const prefix = buildProductCodePrefix({
        productTypeName: typeName,
        teamName: watchAttrs.TEAM || watchAttrs.team || watchAttrs.Team,
        edition: watchAttrs.EDITION || watchAttrs.edition || watchAttrs.Edition,
        sleeve: watchAttrs.SLEEVE_TYPE || watchAttrs.sleeve || watchAttrs.Sleeve,
        collar: watchAttrs.COLLAR_TYPE || watchAttrs.collar || watchAttrs.Collar,
        zipper: !!(watchAttrs.ZIPPER || watchAttrs.zipper),
        player: watchAttrs.PLAYER_NAME || watchAttrs.player || watchAttrs.Player,
        playerNumber: watchAttrs.PLAYER_NUMBER || watchAttrs.player_number || watchAttrs.Number,
        productName: watchName,
      });

      const nextSeq = await getNextProductCodeSequence(organization?.id, prefix);
      const fullCode = formatProductCode(prefix, nextSeq);

      setValue('product_code', fullCode, { shouldValidate: true });
      toast.success(`Generated Product Code: ${fullCode}`);

      // Auto-update empty or default variant SKUs using the generated product code
      const currentVariants = getValues('variants') || [];
      currentVariants.forEach((v, idx) => {
        const suggestedSku = buildVariantSku(fullCode, v.color, v.size);
        setValue(`variants.${idx}.sku`, suggestedSku);
      });
    } catch (err) {
      toast.error('Failed to query database for sequence number');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // ── Load size set into variants ────────────────────────────────
  const loadSizeSet = async (sizeSet: any) => {
    const sizes: string[] = Array.isArray(sizeSet.sizes) ? sizeSet.sizes : [];
    if (sizes.length === 0) return;

    const shouldProceed = fields.length > 1 || (fields.length === 1 && ((fields[0] as any).size))
      ? await confirm({
          title: `Load "${sizeSet.name}" Size Set?`,
          description: `This will ADD ${sizes.length} new variant rows (${sizes.join(', ')}) to your existing variants. You may remove unwanted rows after.`,
          confirmText: 'Add Size Set',
        })
      : true;
    if (!shouldProceed) return;

    // If single empty default row, remove it first
    if (fields.length === 1 && !(fields[0] as any).size && !(fields[0] as any).sku) {
      remove(0);
    }

    sizes.forEach((size) => {
      const currentCode = watchCode || (codePrefix ? `${codePrefix}-001` : 'KQ-PROD-001');
      const suggestedSku = buildVariantSku(currentCode, '', size);
      append({
        size,
        color: '',
        sku: suggestedSku,
        default_cost: 0,
        default_price: 0,
        initial_stock: 0,
        low_stock_level: 5,
      });
    });

    setShowSizeSetMenu(false);
  };

  // ── Smart Variant Duplication (Auto-Advances Size & Recalculates SKU) ─
  const handleDuplicateVariant = (index: number) => {
    const currentVal = getValues(`variants.${index}`);
    const currentSize = currentVal.size || '';
    const nextSize = getNextSize(currentSize);

    const currentCode = watchCode || (codePrefix ? `${codePrefix}-001` : 'KQ-PROD-001');
    const newSku = buildVariantSku(currentCode, currentVal.color, nextSize);

    append({
      ...currentVal,
      id: undefined,
      size: nextSize,
      sku: newSku,
      initial_stock: 0,
    });

    toast.info(`Duplicated row with next size: ${nextSize} (SKU: ${newSku})`);
  };

  const firstType = productTypes?.[0];
  const defaultTypeId = firstType ? ((firstType as any).id || firstType.code || firstType.name) : '';
  const selectedProductTypeId = watch('product_type_id') || defaultTypeId;
  const { data: dynamicAttributes } = useProductTypeAttributes(selectedProductTypeId);

  // Set default product_type_id once loaded if empty
  useEffect(() => {
    if (!isEditing && productTypes && productTypes.length > 0 && !watch('product_type_id')) {
      const ft = productTypes[0];
      if (ft && (ft as any).id) {
        setValue('product_type_id', (ft as any).id);
      }
    }
  }, [productTypes, isEditing, setValue, watch]);


  // Set default target audience & age group if empty
  useEffect(() => {
    if (!isEditing) {
      if (targetAudiences && targetAudiences.length > 0 && !watch('target_audience_id')) {
        const unisex = targetAudiences.find(a => a.code === 'UNISEX' || a.name.toLowerCase() === 'unisex');
        if (unisex) {
          const val = (unisex as any).id || unisex.code;
          setValue('target_audience_id', val);
        }
      }
      if (ageGroups && ageGroups.length > 0 && !watch('age_group_id')) {
        const adult = ageGroups.find(a => a.code === 'ADULT' || a.name.toLowerCase() === 'adult');
        if (adult) {
          const val = (adult as any).id || adult.code;
          setValue('age_group_id', val);
        }
      }
    }
  }, [targetAudiences, ageGroups, isEditing, setValue, watch]);

  // Populate form when editing
  useEffect(() => {
    if (existing) {
      const existingAttrs = existing.attributes && typeof existing.attributes === 'object' ? existing.attributes : {};
      
      // Fallback legacy field mapping if attributes object is empty
      if (existing.team && !existingAttrs.team && !existingAttrs.TEAM) existingAttrs.team = existing.team;
      if (existing.sleeve && !existingAttrs.sleeve && !existingAttrs.SLEEVE_TYPE) existingAttrs.sleeve = existing.sleeve;
      if (existing.material && !existingAttrs.material && !existingAttrs.MATERIAL) existingAttrs.material = existing.material;

      // Match target_audience_id (UUID or Code or Name)
      let matchedAudienceId = existing.target_audience_id ?? existingAttrs?.TARGET_AUDIENCE ?? existingAttrs?.target_audience ?? '';
      if (targetAudiences && targetAudiences.length > 0) {
        const found = targetAudiences.find(
          a => (a as any).id === matchedAudienceId || a.code === matchedAudienceId || a.name.toLowerCase() === matchedAudienceId.toLowerCase()
        );
        if (found) {
          matchedAudienceId = (found as any).id || found.code;
        }
      }

      // Match age_group_id (UUID or Code or Name)
      let matchedAgeGroupId = existing.age_group_id ?? existingAttrs?.AGE_GROUP ?? existingAttrs?.age_group ?? '';
      if (ageGroups && ageGroups.length > 0) {
        const found = ageGroups.find(
          a => (a as any).id === matchedAgeGroupId || a.code === matchedAgeGroupId || a.name.toLowerCase() === matchedAgeGroupId.toLowerCase()
        );
        if (found) {
          matchedAgeGroupId = (found as any).id || found.code;
        }
      }

      reset({
        product_code: existing.product_code,
        name: existing.name,
        category_id: existing.category_id ?? '',
        product_type_id: existing.product_type_id ?? '',
        target_audience_id: matchedAudienceId,
        age_group_id: matchedAgeGroupId,
        description: existing.description ?? '',
        attributes: existingAttrs,
        variants: existing.product_variants && existing.product_variants.length > 0
          ? existing.product_variants.map((v) => ({
            id: v.id,
            size: v.size,
            color: v.color ?? '',
            sku: v.sku,
            default_cost: Number(v.default_cost),
            default_price: Number(v.default_price),
            initial_stock: 0,
            low_stock_level: v.low_stock_level,
          }))
          : [{ size: '', color: '', sku: '', default_cost: 0, default_price: 0, initial_stock: 0, low_stock_level: 5 }],
      });
    }
  }, [existing, targetAudiences, ageGroups, reset]);

  // Handle product type change warning when editing
  const handleProductTypeChange = async (newTypeId: string) => {
    if (isEditing && existing && existing.product_type_id && existing.product_type_id !== newTypeId) {
      const isOk = await confirm({
        title: 'Change Product Type?',
        description: 'Changing the product type will alter available dynamic attributes for this product. Unsaved attribute entries may be hidden.',
        variant: 'destructive',
        confirmText: 'Change Type',
      });
      if (!isOk) return;
    }
    setValue('product_type_id', newTypeId);
  };

  const onSubmit = async (values: ProductFormValues) => {
    // Hard guard: prevents concurrent submissions regardless of React state timing
    if (isInFlight.current) return;
    isInFlight.current = true;

    try {
      const { variants, ...productData } = values;

      // Check required dynamic attributes
      if (dynamicAttributes) {
        for (const attr of dynamicAttributes) {
          if (attr.is_required && !attr.is_variant_level) {
            const attrKey = attr.code || attr.id || attr.name;
            const val = values.attributes?.[attrKey] || values.attributes?.[attr.name];
            if (!val || !String(val).trim()) {
              toast.error(`"${attr.name}" is required for selected product type`);
              return;
            }
          }
        }
      }

      if (isEditing && id) {
        // 1. Update product main record
        await updateProduct.mutateAsync({ id, ...productData });

        // 2. Update existing variants and create any newly added variants
        if (variants && variants.length > 0) {
          for (const v of variants) {
            const vId = (v as any).id;
            if (vId) {
              await updateVariant.mutateAsync({
                id: vId,
                sku: v.sku,
                size: v.size,
                color: v.color ?? null,
                default_cost: v.default_cost,
                default_price: v.default_price,
                low_stock_level: v.low_stock_level,
              });
            } else {
              await createVariant.mutateAsync({
                ...v,
                product_id: id,
              });
            }
          }
        }
      } else {
        if (!productData.product_code || !productData.product_code.trim()) {
          const selectedType = productTypes?.find((pt: any) => ((pt as any).id || pt.code || pt.name) === productData.product_type_id);
          const typeName = selectedType?.name || selectedType?.code || '';

          const prefix = buildProductCodePrefix({
            productTypeName: typeName,
            teamName: productData.attributes?.TEAM || productData.attributes?.team,
            edition: productData.attributes?.EDITION || productData.attributes?.edition,
            sleeve: productData.attributes?.SLEEVE_TYPE || productData.attributes?.sleeve,
            collar: productData.attributes?.COLLAR_TYPE || productData.attributes?.collar,
            zipper: !!(productData.attributes?.ZIPPER || productData.attributes?.zipper),
            player: productData.attributes?.PLAYER_NAME || productData.attributes?.player,
            playerNumber: productData.attributes?.PLAYER_NUMBER || productData.attributes?.player_number,
            productName: productData.name,
          });

          const nextSeq = await getNextProductCodeSequence(organization?.id, prefix);
          productData.product_code = formatProductCode(prefix, nextSeq);
        }

        const product = await createProduct.mutateAsync(productData);

        // Guarantee all variant SKUs are unique within submission
        const seenSkus = new Set<string>();
        const sanitizedVariants = (variants || []).map((v) => {
          let sku = (v.sku || '').trim();
          if (!sku) {
            sku = buildVariantSku(product.product_code || productData.product_code || 'PROD', v.color, v.size);
          }
          let uniqueSku = sku;
          let counter = 1;
          while (seenSkus.has(uniqueSku)) {
            uniqueSku = `${sku}-${counter}`;
            counter++;
          }
          seenSkus.add(uniqueSku);
          return { ...v, sku: uniqueSku };
        });

        // Create variants individually so partial failures are recoverable
        let failedCount = 0;
        for (const v of sanitizedVariants) {
          try {
            await createVariant.mutateAsync({ ...v, product_id: product.id });
          } catch {
            failedCount++;
          }
        }

        if (failedCount > 0) {
          // Partial success: product created but some variants failed.
          // Do NOT navigate — let the user know and keep form open for retry.
          toast.error(
            `Product "${productData.name}" was created, but ${failedCount} variant${failedCount !== 1 ? 's' : ''} failed to save. Open the product from the list to add them manually.`,
            { duration: 8000 }
          );
          return;
        }
      }

      toast.success(isEditing ? 'Product updated successfully' : 'Product created successfully');
      navigate('/products');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      // Surface a human-readable conflict message instead of raw Postgres error
      if (msg.includes('23505') || msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('duplicate')) {
        toast.error('A product with this code already exists. The system attempted to generate a new code but encountered a conflict. Please review your product details and try again.');
      } else {
        toast.error(msg);
      }
    } finally {
      isInFlight.current = false;
    }
  };

  if (loadingProduct && isEditing) return <LoadingState message="Loading product…" />;
  if (productError) return <ErrorState message={productError.message} />;

  const isWorking = isSubmitting || createProduct.isPending || updateProduct.isPending;

  // Render attribute input helper
  const renderAttributeInput = (attr: Attribute & { is_required?: boolean }) => {
    const attrKey = attr.code || attr.id || attr.name;
    const fieldId = `attr-${attrKey}`;

    // 1. Team attribute -> pull from teams master
    if (attrKey === 'TEAM' || attr.name.toLowerCase() === 'team') {
      return (
        <Controller
          control={control}
          name={`attributes.${attrKey}`}
          render={({ field: selectField, fieldState: selectState }) => (
            <Select
              id={fieldId}
              {...selectField}
              className={inputCls}
              aria-invalid={selectState.invalid || undefined}
              value={selectField.value ?? ''}
              helpLabel={attr.name}
            >
              <option value="">-- Select Team --</option>
              {teams?.map((t) => (
                <option key={t.id || t.code} value={t.name}>
                  {t.name} ({t.sport})
                </option>
              ))}
            </Select>
          )}
        />
      );
    }

    // 2. Select with options
    if (attr.input_type === 'select' && attr.options && Array.isArray(attr.options)) {
      return (
        <Controller
          control={control}
          name={`attributes.${attrKey}`}
          render={({ field: selectField, fieldState: selectState }) => (
            <Select
              id={fieldId}
              {...selectField}
              className={inputCls}
              aria-invalid={selectState.invalid || undefined}
              value={selectField.value ?? ''}
              helpLabel={attr.name}
            >
              <option value="">-- Select {attr.name} --</option>
              {attr.options.map((opt: string) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          )}
        />
      );
    }

    // 3. Number
    if (attr.input_type === 'number') {
      return (
        <Input
          id={fieldId}
          type="number"
          {...register(`attributes.${attrKey}`)}
          placeholder={`Enter ${attr.name}`}
          className={inputCls}
          helpLabel={attr.name}
        />
      );
    }

    // 4. Fallback text input
    return (
      <Input
        id={fieldId}
        type="text"
        {...register(`attributes.${attrKey}`)}
        placeholder={`e.g. ${attr.name}`}
        className={inputCls}
        helpLabel={attr.name}
      />
    );
  };

  // Filter dynamic attributes for product level vs variant level
  const productLevelAttributes = (dynamicAttributes ?? []).filter((a: Attribute & { is_required?: boolean }) => !a.is_variant_level);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title={isEditing ? 'Edit Product' : 'New Product'}
        description={isEditing ? 'Update product details and dynamic attributes' : 'Add a new product with master configuration attributes'}
        backTo="/products"
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
          <form id="product-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-8">
            
            {/* STEP 1: BASIC INFORMATION */}
            <div className="obsidian-surface rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-6 flex items-center gap-3 border-b border-border/40 pb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">Basic Information</h2>
                  <p className="text-sm text-muted-foreground">Core details about the product identity.</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field id="product-name" label="Product Name" required error={errors.name?.message}>
                    <Input
                      id="product-name"
                      {...register('name')}
                      placeholder="e.g. Spain Away Half Sleeve Jersey | Pedri 20"
                      className={inputCls}
                      aria-invalid={!!errors.name}
                      helpLabel="Product Name"
                    />
                  </Field>

                  <Field id="product-code" label="Product Code" required error={errors.product_code?.message}>
                    {isEditing ? (
                      <div className="space-y-1">
                        <div className="relative flex items-center">
                          <Input
                            id="product-code"
                            {...register('product_code')}
                            readOnly
                            disabled
                            className={`${inputCls} uppercase bg-muted/60 text-muted-foreground font-mono font-bold tracking-wider cursor-not-allowed pl-9`}
                            helpLabel="Product Code"
                          />
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                        </div>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Lock className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                          Code locked after save to protect inventory & sales records.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            id="product-code"
                            {...register('product_code')}
                            placeholder="e.g. KQ-JER-ESP-AWAY-HS-PEDRI-20-001"
                            className={`${inputCls} uppercase font-mono font-bold tracking-wider flex-1`}
                            aria-invalid={!!errors.product_code}
                            helpLabel="Product Code"
                          />
                          <button
                            type="button"
                            onClick={autoGenerateCode}
                            disabled={isGeneratingCode}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold text-primary transition-all hover:bg-primary/20 hover:border-primary/40 shrink-0 shadow-sm disabled:opacity-50"
                          >
                            {isGeneratingCode ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Zap className="h-3.5 w-3.5" />
                            )}
                            {watchCode ? 'Regenerate Code' : 'Generate Code'}
                          </button>
                        </div>

                        {/* Live Code Preview Banner */}
                        <div className="rounded-lg bg-muted/30 p-2.5 border border-border/40 text-xs flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                            <span className="text-muted-foreground font-medium">Code Pattern:</span>
                            <span className="font-mono font-bold text-foreground tracking-wide bg-background px-2 py-0.5 rounded border border-border/50">
                              {codePrefix ? `${codePrefix}-???` : 'KQ-PROD-001'}
                            </span>
                          </div>
                          <span className="text-[11px] text-muted-foreground italic">
                            (Sequence assigned from database)
                          </span>
                        </div>
                      </div>
                    )}
                  </Field>
                </div>

                {/* Structured Identity Block for Code Generation */}
                <div className="bg-primary/5 p-4 rounded-xl border border-primary/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4" />
                      Structured Product Identity (Auto-Generates Code)
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Structured fields build consistent product codes
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field id="attr-EDITION" label="Edition / Version">
                      <Controller
                        control={control}
                        name={'attributes.EDITION'}
                        render={({ field: selectField, fieldState: selectState }) => (
                          <Select
                            id="attr-EDITION"
                            {...selectField}
                            className={inputCls}
                            aria-invalid={selectState.invalid || undefined}
                            value={selectField.value ?? ''}
                            helpLabel="Edition / Version"
                          >
                            <option value="">-- Select Edition --</option>
                            <option value="Home">Home</option>
                            <option value="Away">Away</option>
                            <option value="Third">Third</option>
                            <option value="Retro">Retro</option>
                            <option value="Special Edition">Special Edition</option>
                            <option value="Training">Training</option>
                            <option value="Goalkeeper">Goalkeeper</option>
                          </Select>
                        )}
                      />
                    </Field>

                    <Field id="attr-PLAYER_NAME" label="Player Name (Optional)">
                      <Input
                        id="attr-PLAYER_NAME"
                        type="text"
                        {...register('attributes.PLAYER_NAME')}
                        placeholder="e.g. Pedri / Messi / Ronaldo"
                        className={inputCls}
                        helpLabel="Player Name (Optional)"
                      />
                    </Field>

                    <Field id="attr-PLAYER_NUMBER" label="Player Number (Optional)">
                      <Input
                        id="attr-PLAYER_NUMBER"
                        type="number"
                        min="0"
                        max="99"
                        {...register('attributes.PLAYER_NUMBER')}
                        placeholder="e.g. 20 / 10 / 7"
                        className={inputCls}
                        helpLabel="Player Number (Optional)"
                      />
                    </Field>
                  </div>
                </div>

                {/* Classification Row */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 bg-muted/20 p-5 rounded-lg border border-border/30">
                  <Field id="product-category" label="Subcategory / collection">
                    <Controller
                      control={control}
                      name={'category_id'}
                      render={({ field: selectField, fieldState: selectState }) => (
                        <Select
                          id="product-category"
                          {...selectField}
                          className={inputCls}
                          aria-invalid={selectState.invalid || undefined}
                          value={selectField.value ?? ''}
                          helpLabel="Subcategory / collection"
                        >
                          <option value="">-- Select Category --</option>
                          {categories?.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </Field>

                  <Field id="product-type" label="Product category" required error={errors.product_type_id?.message}>
                    <Controller
                      control={control}
                      name={'product_type_id'}
                      render={({ field: selectField, fieldState: selectState }) => (
                        <Select
                          id="product-type"
                          {...selectField}
                          value={selectedProductTypeId}
                          onChange={(e) => {
                            selectField.onChange(e);
                            handleProductTypeChange(e.target.value);
                          }}
                          className={inputCls}
                          aria-invalid={selectState.invalid || undefined}
                          helpLabel="Product category"
                        >
                          {productTypes?.map((pt) => {
                            const ptId = (pt as any).id || pt.code || pt.name;
                            return (
                              <option key={ptId} value={ptId}>
                                {pt.name}
                              </option>
                            );
                          })}
                        </Select>
                      )}
                    />
                  </Field>

                  <Field id="target-audience" label="Target Audience / Gender">
                    <Controller
                      control={control}
                      name={'target_audience_id'}
                      render={({ field: selectField, fieldState: selectState }) => (
                        <Select
                          id="target-audience"
                          {...selectField}
                          className={inputCls}
                          aria-invalid={selectState.invalid || undefined}
                          value={selectField.value ?? ''}
                          helpLabel="Target Audience / Gender"
                        >
                          <option value="">-- Select Audience --</option>
                          {targetAudiences?.map((ta) => (
                            <option key={ta.id || ta.code} value={ta.id || ta.code}>
                              {ta.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </Field>

                  <Field id="age-group" label="Age Group">
                    <Controller
                      control={control}
                      name={'age_group_id'}
                      render={({ field: selectField, fieldState: selectState }) => (
                        <Select
                          id="age-group"
                          {...selectField}
                          className={inputCls}
                          aria-invalid={selectState.invalid || undefined}
                          value={selectField.value ?? ''}
                          helpLabel="Age Group"
                        >
                          <option value="">-- Select Age Group --</option>
                          {ageGroups?.map((ag) => (
                            <option key={ag.id || ag.code} value={ag.id || ag.code}>
                              {ag.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </Field>
                </div>

                <Field id="product-desc" label="Description" error={errors.description?.message}>
                  <Textarea
                    id="product-desc"
                    {...register('description')}
                    rows={3}
                    placeholder="Provide an engaging description for this product..."
                    className="flex w-full rounded-lg border border-input/60 bg-background/50 px-3 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary shadow-sm resize-y"
                    helpLabel="Description"
                  />
                </Field>
              </div>
            </div>

            {/* STEP 2: DYNAMIC PRODUCT-SPECIFIC ATTRIBUTES */}
            <div className="obsidian-surface rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-6 flex items-center justify-between border-b border-border/40 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
                    <Tags className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-foreground">Product Attributes</h2>
                    <p className="text-sm text-muted-foreground">Dynamic fields based on product type.</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 px-2.5 py-1 rounded-full border border-indigo-500/20">
                  <Sparkles className="h-3.5 w-3.5" />
                  Configured for {productTypes?.find(p => ((p as any).id || p.code || p.name) === selectedProductTypeId)?.name || 'Product Type'}
                </span>
              </div>

              {productLevelAttributes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center bg-muted/10 rounded-lg border border-dashed border-border/60">
                  <Tags className="h-8 w-8 text-muted-foreground/40 mb-3" />
                  <p className="text-sm font-medium text-foreground/80">No Specific Attributes</p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                    There are no product-level attributes assigned to this product type in the Master Configuration.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {productLevelAttributes.map((attr: Attribute & { is_required?: boolean }) => {
                    const attrKey = attr.code || attr.id || attr.name;
                    return (
                      <Field
                        key={attrKey}
                        id={`attr-${attrKey}`}
                        label={attr.name}
                        required={attr.is_required}
                      >
                        {renderAttributeInput(attr)}
                      </Field>
                    );
                  })}
                </div>
              )}
            </div>

            {/* STEP 3: VARIANTS & INVENTORY */}
            <div className="obsidian-surface rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between border-b border-border/40 pb-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-1.5">
                      Variants & Inventory
                      <span className="text-destructive">*</span>
                    </h2>
                    <p className="text-sm text-muted-foreground">Manage SKUs, sizes, colors, and pricing.</p>
                  </div>
                </div>
<div className="flex items-center gap-2">
                  {/* Load from Size Set */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowSizeSetMenu(!showSizeSetMenu)}
                      className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                    >
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      Load Size Set
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${showSizeSetMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showSizeSetMenu && (
                      <div className="obsidian-panel absolute right-0 top-full mt-1 z-20 w-56 rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-border/40">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2">Choose Size Set</p>
                        </div>
                        {(sizeSets ?? []).map((ss: any) => {
                          const sizes: string[] = Array.isArray(ss.sizes) ? ss.sizes : [];
                          return (
                            <button
                              key={ss.code || ss.id}
                              type="button"
                              onClick={() => loadSizeSet(ss)}
                              className="flex w-full flex-col gap-0.5 px-3 py-2.5 text-left text-sm hover:bg-muted/60 transition-colors"
                            >
                              <span className="font-medium text-foreground">{ss.name}</span>
                              <span className="text-[11px] text-muted-foreground">{sizes.join(', ')}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      append({ size: '', color: '', sku: '', default_cost: 0, default_price: 0, initial_stock: 0, low_stock_level: 5 })
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:border-primary/30"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Add Row
                  </button>
                </div>
              </div>

              {errors.variants?.root && (
                <div className="mb-4 rounded-md bg-destructive/10 p-3 border border-destructive/20 text-sm text-destructive flex items-center gap-2">
                  <span className="font-semibold">Error:</span> {errors.variants.root.message}
                </div>
              )}

              <div className="space-y-4">
                {/* Desktop Header Row (Hidden on mobile) */}
                {fields.length > 0 && (
                  <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase bg-muted/40 rounded-t-lg border-b border-border/50">
                    <div className="col-span-2">Size *</div>
                    <div className="col-span-2">Color / Option</div>
                    <div className="col-span-2">SKU *</div>
                    <div className="col-span-2">Cost (₹) *</div>
                    <div className="col-span-2">Sell Price (₹) *</div>
                    <div className="col-span-2 flex justify-between">
                      <span>Low Stock</span>
                      <span>Action</span>
                    </div>
                  </div>
                )}

                <div className="space-y-4 lg:space-y-0 lg:divide-y lg:divide-border/40 lg:border lg:border-border/50 lg:rounded-b-lg">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="relative rounded-lg border border-border/60 bg-muted/10 p-4 lg:p-0 lg:border-none lg:bg-transparent lg:rounded-none transition-colors hover:bg-muted/20"
                    >
                      {/* Mobile Label */}
                      <div className="mb-4 flex items-center justify-between lg:hidden border-b border-border/30 pb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-primary">
                          Variant {index + 1} {(field as any).id ? '(Existing)' : '(New)'}
                        </span>
                        <div className="flex items-center gap-1">
<button
                            type="button"
                            onClick={() => handleDuplicateVariant(index)}
                            className="rounded p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                            title="Duplicate row with next size"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          {fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                              title="Remove variant"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:gap-3 lg:p-3 lg:items-start">
                        <Field id={`variant-${index}-size`} label="Size" required error={errors.variants?.[index]?.size?.message} className="lg:col-span-2 lg:[&>label]:hidden">
                          <Input
                            id={`variant-${index}-size`}
                            {...register(`variants.${index}.size`)}
                            placeholder="e.g. S / M / L"
                            className={inputCls}
                            helpLabel="Size"
                          />
                        </Field>
                        
                        <Field id={`variant-${index}-color`} label="Color / Option" error={errors.variants?.[index]?.color?.message} className="lg:col-span-2 lg:[&>label]:hidden">
                          <Input
                            id={`variant-${index}-color`}
                            {...register(`variants.${index}.color`)}
                            placeholder="e.g. Black"
                            className={inputCls}
                            helpLabel="Color / Option"
                          />
                        </Field>
                        
                        <Field id={`variant-${index}-sku`} label="SKU" required error={errors.variants?.[index]?.sku?.message} className="lg:col-span-2 lg:[&>label]:hidden">
                          <Input
                            id={`variant-${index}-sku`}
                            {...register(`variants.${index}.sku`)}
                            placeholder="SKU"
                            className={`${inputCls} uppercase`}
                            helpLabel="SKU"
                          />
                        </Field>
                        
                        <Field id={`variant-${index}-cost`} label="Cost (₹)" required error={errors.variants?.[index]?.default_cost?.message} className="lg:col-span-2 lg:[&>label]:hidden">
                          <Controller
                            name={`variants.${index}.default_cost`}
                            control={control}
                            render={({ field: f }) => (
                              <Input
                                id={`variant-${index}-cost`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={f.value}
                                onChange={(e) => f.onChange(parseFloat(e.target.value) || 0)}
                                className={inputCls}
                                helpLabel="Cost (₹)"
                              />
                            )}
                          />
                        </Field>
                        
                        <Field id={`variant-${index}-price`} label="Sell Price (₹)" required error={errors.variants?.[index]?.default_price?.message} className="lg:col-span-2 lg:[&>label]:hidden">
                          <Controller
                            name={`variants.${index}.default_price`}
                            control={control}
                            render={({ field: f }) => (
                              <Input
                                id={`variant-${index}-price`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={f.value}
                                onChange={(e) => f.onChange(parseFloat(e.target.value) || 0)}
                                className={inputCls}
                                helpLabel="Sell Price (₹)"
                              />
                            )}
                          />
                        </Field>
                        
                        <div className="lg:col-span-2 flex items-start gap-2">
                          <div className="flex-1">
                            <Field id={`variant-${index}-low`} label="Low Stock Alert" error={errors.variants?.[index]?.low_stock_level?.message} className="lg:[&>label]:hidden">
                              <Input
                                id={`variant-${index}-low`}
                                type="number"
                                min="0"
                                {...register(`variants.${index}.low_stock_level`, { valueAsNumber: true })}
                                className={inputCls}
                                helpLabel="Low Stock Alert"
                              />
                            </Field>
                          </div>
                          
                          {/* Desktop Actions */}
                          <div className="hidden lg:flex mt-1 items-center gap-1">
<button
                              type="button"
                              onClick={() => handleDuplicateVariant(index)}
                              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                              title="Duplicate row with next size"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            {fields.length > 1 && (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                                title="Remove variant"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* STICKY BOTTOM ACTION BAR */}
      <div className="z-10 shrink-0 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_15px_-5px_rgba(0,0,0,0.05)]">
        <div className="mx-auto flex max-w-5xl items-center justify-end gap-3 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="inline-flex h-11 items-center justify-center rounded-lg border border-border bg-background px-6 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="product-form"
            disabled={isWorking}
            onKeyDown={(e) => { if (isWorking && e.key === 'Enter') e.preventDefault(); }}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-8 text-sm font-medium text-primary-foreground shadow-md shadow-primary/20 transition-colors hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isWorking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isWorking ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}
